import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Checkbox,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import ProjectAnalytics from './ProjectAnalytics';
import IntegrationsSection from './IntegrationsSection';
import { Edit, Trash2, User, ExternalLink } from 'lucide-react';
import API_BASE from '../../apiBase';

const ProjectCard = ({
  project,
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
  isSelected = false,
  onSelect
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(copyTimeoutRef.current);
  }, []);

  const handleCopy = (textToCopy) => {
    if (!textToCopy) return;
    navigator.clipboard.writeText(textToCopy);
    setCopySuccess(true);

    clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => {
      setCopySuccess(false);
    }, 2000);
  };

  const getTruncatedKey = (key) => {
    if (!key) return '';
    if (key.length <= 12) return key;
    return key.slice(0, 6) + '...' + key.slice(-4);
  };

  const webhookUrl = `${API_BASE}/api/webhook/${project.repository_type === 'azure' ? 'azuredevops' : project.repository_type}?api_key=${project.api_key}`;

  return (
    <Card
      sx={{
        height: '100%',
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: '1px 1px 3px rgba(0, 0, 0, 0.1)',
        border: isSelected ? '1px solid #00bfff' : '2px solid transparent',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '3px 4px 12px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 4 }}>
        {/* Project Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, }}>
              {/* {onSelect && (
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => onSelect(e.target.checked)}
                  sx={{
                    color: '#000',
                    mt: 1,
                    '&.Mui-checked': {
                      color: '#00bfff',
                    },
                    p: 0,
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.25rem',
                    },
                  }}
                />
              )} */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#1f1f1f',
                  fontSize: '1.25rem',
                  lineHeight: '1.75rem',
                  transition: 'color 0.2s',
                  '.MuiCard-root:hover &': {
                    color: '#00bfff',
                  },
                }}
              >
                {project.name}
              </Typography>
              {project.subscription_plan_name && (
                <Box sx={{ ml: 1, px: 1, py: 0.25, borderRadius: 1, bgcolor: '#EEF2FF', border: '1px solid #E0E7FF' }}>
                  <Typography variant="caption" sx={{ color: '#4338CA', fontWeight: 600 }}>
                    {project.subscription_plan_name}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: '#737373' }}>
              <User size={16} />
              <Typography
                variant="body2"
                sx={{
                  color: '#737373',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                }}
              >
                Created by {project.created_by_info?.name || 'Unknown'}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: '#737373',
                fontSize: '0.875rem',
                lineHeight: 1.5,
              }}
            >
              {project.description || 'No description available'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
              '.MuiCard-root:hover &': {
                opacity: 1,
              }
            }}
          >
            {/* <Tooltip title="Open project">
              <IconButton
                size="small"
                // onClick={() => window.open(`/projects/${project.id}`, '_blank')}
                sx={{
                  '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.1)' },
                }}
              >
                <ExternalLink  size={16}/>
              </IconButton>
            </Tooltip> */}
            <Tooltip title="Edit project">
              {!canEdit ? (
                <span>
                  <IconButton
                    size="small"
                    disabled={true}
                  >
                    <Edit size={16}/>
                  </IconButton>
                </span>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => onEdit(project)}
                >
                  <Edit size={16}/>
                </IconButton>
              )}
            </Tooltip>
            <Tooltip title="Delete project">
              {!canDelete ? (
                <span>
                  <IconButton
                    size="small"
                    disabled={true}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' },
                    }}
                  >
                    <Trash2 size={16}/>
                  </IconButton>
                </span>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => onDelete(project.id)}
                  sx={{
                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' },
                  }}
                >
                  <Trash2 size={16}/>
                </IconButton>
              )}
            </Tooltip>
          </Box>
        </Box>

        {/* Webhook URL Section */}
        <Box sx={{ mb: 3, borderRadius: 2, border: '1px solid #f0f0f0' }}>
          <Typography
            variant="body2"
            sx={{
              color: '#737373',
              fontSize: '0.875rem',
              fontWeight: 600,
              mb: 1,
            }}
          >
            Webhook URL
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              value={webhookUrl}
              fullWidth
              size="small"
              InputProps={{
                readOnly: true,
                sx: {
                  fontSize: '0.675rem',
                  bgcolor: '#F8FAFC',
                  '& fieldset': {
                    borderColor: '#E2E8F0',
                  },
                },
              }}
            />
            <Tooltip title="Copy webhook URL">
              <IconButton
                size="small"
                onClick={() => handleCopy(webhookUrl)}
                sx={{
                  bgcolor: '#F1F5F9',
                  '&:hover': { bgcolor: '#E2E8F0' },
                }}
              >
                <CopyIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Integrations Section */}
        <IntegrationsSection project={project} />

        {/* Analytics Section */}
        <ProjectAnalytics project={project} />
      </CardContent>
    </Card>
  );
};

export default ProjectCard;